import React from 'react'
import PropTypes from 'prop-types'
import { ErrorMessage, FormikContext } from 'formik'

import styles from './checkbox.css'
import clsx from 'clsx'

class Checkbox extends React.Component {
  constructor(props) {
    super(props)

    const { checked } = props
    this.state = {
      checked: checked || false,
      errorMessage: null,
    }

    this.inputRef = React.createRef()
    this.onCheckboxChanged = this.onCheckboxChanged.bind(this)
  }

  // Deliberately kept as the UNSAFE_ lifecycle instead of migrating to getDerivedStateFromProps:
  // this hook must run on PARENT re-renders only. getDerivedStateFromProps also runs after the
  // component's own setState, which would immediately overwrite the optimistic local toggle in
  // onCheckboxChanged before the parent has fed the new value back down. A faithful migration
  // needs the "remember previous props" pattern plus a pass over all call sites; until then the
  // rename removes the console warning without changing a single semantics (no StrictMode here).
  UNSAFE_componentWillReceiveProps(newProps) {
    const { errorMessage: newErrorMessage, checked } = newProps
    const { errorMessage: oldErrorMessage } = this.state
    const errorMessage = oldErrorMessage || newErrorMessage || ''

    const state = { errorMessage }

    if (checked !== undefined && checked !== null) {
      state.checked = checked
    }

    this.setState({ ...state })
  }

  onCheckboxChanged(event) {
    const { checked } = this.state
    const { disabled, name, onChange } = this.props

    const modifiers = {
      shift: event.getModifierState('Shift'),
      control: event.getModifierState('Control'),
      alt: event.getModifierState('Alt'),
    }

    event.nativeEvent.stopPropagation()
    event.nativeEvent.preventDefault()
    if (disabled) return

    const newValue = !checked
    this.setState({ checked: newValue, errorMessage: null }, () => {
      const fieldHelper = this.context?.getFieldHelpers(name)
      fieldHelper && fieldHelper.setValue(newValue)
      onChange && onChange(newValue, name, modifiers)
    })
  }

  render() {
    const { errorMessage } = this.state
    const {
      name,
      customClass,
      className,
      customVisualClass,
      customErrorClass,
      disabled,
      children,
      inputPosition,
      useFormik,
      dataQsId,
    } = this.props
    const containerClass = clsx(styles.container, customClass, className)
    const errorClass = `${styles.error} ${customErrorClass || ''}`
    const disabledClass = disabled ? styles.disabled : ''
    const hasErrorMessage = !!errorMessage
    const visualClass = `${styles.visual} ${customVisualClass || ''}`
    const inputWrapperClassNames = [styles.inputWrapper, disabledClass]

    if (inputPosition === 'top') {
      inputWrapperClassNames.push(styles.positionStart)
    }

    const field = useFormik === undefined || useFormik ? this.context?.getFieldProps(name) : null

    const checked = field ? field.value : this.state.checked

    const inputClass = `${styles.input} ${checked === 'indeterminate' ? styles.indeterminate : null}`

    const errorElm = useFormik
      ? <ErrorMessage name={name} className={styles.error} component="div" render={() => <div className={styles.error}>{errorMessage}</div>} />
      : hasErrorMessage
        ? <div className={errorClass}>{errorMessage}</div>
        : null

    return (
      <div className={containerClass} data-qs-id={dataQsId}>
        <div className={inputWrapperClassNames.join(' ')} onClick={this.onCheckboxChanged}>
          <input
            ref={this.inputRef}
            name={name}
            type="checkbox"
            className={inputClass}
            checked={checked}
            readOnly={true}
            disabled={disabled}
            {...field}
          >
          </input>
          <div className={visualClass} />
          {children}
        </div>
        {errorElm}
      </div>
    )
  }
}

Checkbox.contextType = FormikContext

Checkbox.propTypes = {
  name: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
  inputPosition: PropTypes.string,
  useFormik: PropTypes.bool,
  dataQsId: PropTypes.string.isRequired,
}
Checkbox.defaultProps = {
  inputPosition: 'center',
}

export default Checkbox
